import { Component } from '@theme/component'
import { animPrimary, durationDefault, staggerDefault } from '@theme/gsap'
import { useIntersection } from '@theme/use-intersection'
import { useReduced } from '@theme/utils'

const root = globalThis
const theme = root.theme || {}
root.theme = theme
theme.config = theme.config || {}
theme.InitWhenVisible ??= class {
  constructor(callback) {
    if (typeof callback === 'function') {
      callback()
    }
  }
}

class HeroSlideshow extends Component {
  constructor() {
    super()

    this.currentIndex = 0
    this.timer = null
    this.unobserve = null
    this.isAnimating = false
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchActive = false
    this.touchTargets = []
    this.splitInitialized = false
    this.splitFontsCheck = false
  }

  connectedCallback() {
    super.connectedCallback?.()
    if (theme.config.isTouch) {
      new theme.InitWhenVisible(this.init)
      return
    }

    const intersection = useIntersection({
      rootMargin: '200px 0px 200px 0px'
    })
    this.unobserve = intersection.observe(this, (entry, obs) => {
      if (!entry.isIntersecting) {
        return
      }
      this.init()
      obs.unobserve(this)
    })

    if (!intersection.size) {
      this.init()
    }
  }

  get slides() {
    this._slides =
      this._slides || Array.from(this.querySelectorAll('.hero-slider__slide'))
    return this._slides
  }

  get dots() {
    return this.querySelectorAll('.hero-slider__dot')
  }

  get autoplay() {
    return this.dataset.autoplay === 'true' || this.hasAttribute('autoplay')
  }

  get speed() {
    const autoplaySpeed = Number.parseFloat(
      this.dataset.autoplayInterval ?? this.getAttribute('autoplay-speed')
    )
    return Number.isFinite(autoplaySpeed) && autoplaySpeed > 0
      ? autoplaySpeed * 1000
      : 5000
  }

  init = () => {
    if (this.initialized) {
      return
    }
    this.initialized = true
    const firstSlide = this.slides[0]

    if (!firstSlide) {
      this.setAttribute('loaded', '')
      return
    }

    this.initSplitText()

    if (!this.splitFontsCheck && document?.fonts?.ready) {
      this.splitFontsCheck = true
      document.fonts.ready.then(() => {
        this.initSplitText()
      })
    }

    if (this.slides.length > 1) {
      this.setupControls()
      this.setupKeyboard()
      this.setupTouch()

      this.syncSlideState(0)
      gsap.set(this.slides, { autoAlpha: 0, xPercent: 0, zIndex: 0 })
      gsap.set(firstSlide, { autoAlpha: 1, xPercent: 0, zIndex: 1 })

      this.setAttribute('loaded', '')

      if (this.autoplay) {
        this.playPlayer()
      }
      return
    }

    this.setAttribute('loaded', '')
    this.syncSlideState(0)
    gsap.set(firstSlide, { autoAlpha: 1, xPercent: 0, zIndex: 1 })
  }

  setupControls = () => {
    this.prevButton = this.querySelector('button[is="previous-button"]')
    this.nextButton = this.querySelector('button[is="next-button"]')
    this.isPlaying = this.autoplay

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrev)
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNext)
    }

    this.dots?.forEach((dot) => {
      const dotIndex = Number.parseInt(dot.dataset.index, 10)
      if (Number.isNaN(dotIndex)) {
        return
      }
      dot.addEventListener('click', this.handleDotClick)
    })

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.handleBlockSelect)
    }
  }

  handlePrev = () => {
    this.pausePlayer()
    this.previous()
  }

  handleNext = () => {
    this.pausePlayer()
    this.next()
  }

  handleDotClick = (event) => {
    const dotIndex = Number.parseInt(event.currentTarget.dataset.index, 10)
    if (this.currentIndex === dotIndex) return
    this.pausePlayer()
    this.select(dotIndex)
  }

  handleBlockSelect = (event) => {
    const slide = event.target.closest('.hero-slider__slide')
    const index = this.slides.indexOf(slide)
    if (index !== -1) {
      this.select(index)
      this.pausePlayer()
    }
  }

  setupKeyboard = () => {
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0')
    }
    this.addEventListener('keydown', this.handleKeyDown)
  }

  setupTouch = () => {
    if (!theme.config.isTouch) {
      return
    }
    this.addEventListener('click', this.handleClick, true)
    this.touchTargets = Array.from(this.querySelectorAll('.hero-slider__slide'))
    this.touchTargets.forEach((target) => {
      target.addEventListener('touchstart', this.handleTouchStart, {
        passive: true
      })
      target.addEventListener('touchend', this.handleTouchEnd)
      target.addEventListener('touchcancel', this.handleTouchEnd)
    })
  }

  handleClick = (event) => {
    if (this.preventClick) {
      event.preventDefault()
      event.stopPropagation()
      this.preventClick = false
    }
  }

  handleKeyDown = (event) => {
    if (this.slides.length < 2) {
      return
    }
    if (event.defaultPrevented) {
      return
    }
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return
    }

    const target = event.target
    if (target instanceof HTMLElement) {
      const tagName = target.tagName
      if (
        target.isContentEditable ||
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT'
      ) {
        return
      }
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.pausePlayer()
      this.next()
      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.pausePlayer()
      this.previous()
    }
  }

  handleTouchStart = (event) => {
    if (event.touches?.length > 1) {
      return
    }
    const touch = event.touches?.[0]
    if (!touch) {
      return
    }
    this.touchActive = true
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
  }

  handleTouchEnd = (event) => {
    if (!this.touchActive) {
      return
    }
    this.touchActive = false

    const touch = event.changedTouches?.[0]
    if (!touch) {
      return
    }

    const deltaX = touch.clientX - this.touchStartX
    const deltaY = touch.clientY - this.touchStartY
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    if (absX < 40 || absX < absY) {
      return
    }

    this.preventClick = true
    setTimeout(() => {
      this.preventClick = false
    }, 300)

    this.pausePlayer()
    if (deltaX > 0) {
      this.previous()
    } else {
      this.next()
    }
  }

  next = () => {
    if (this.slides.length < 2) {
      return
    }
    const nextIndex = (this.currentIndex + 1) % this.slides.length
    this.goTo(nextIndex, 1)
  }

  previous = () => {
    if (this.slides.length < 2) {
      return
    }
    const prevIndex =
      (this.currentIndex - 1 + this.slides.length) % this.slides.length
    this.goTo(prevIndex, -1)
  }

  select = (index) => {
    const direction = index > this.currentIndex ? 1 : -1
    this.goTo(index, direction)
  }

  goTo = (index, direction = index > this.currentIndex ? 1 : -1) => {
    if (
      this.isAnimating ||
      index === this.currentIndex ||
      !this.slides[index]
    ) {
      return
    }
    this.initSplitText()
    if (typeof gsap === 'undefined' || useReduced()) {
      this.currentIndex = index
      this.syncSlideState(index)
      this.updateDots()
      this.dispatchSlideChange()
      return
    }

    this.isAnimating = true
    const fromSlide = this.slides[this.currentIndex]
    const toSlide = this.slides[index]
    const motionDirection = direction > 0 ? 1 : -1

    if (!toSlide.split) {
      this.createSplit(toSlide)
    }

    this.currentIndex = index
    this.syncSlideState(index, this.slides.indexOf(fromSlide))
    this.updateDots()
    this.dispatchSlideChange()
    this.toggleAttribute('data-animating', true)

    const timeline = gsap.timeline({
      defaults: {
        duration: durationDefault,
        ease: animPrimary
      },
      onComplete: () => {
        this.isAnimating = false
        this.removeAttribute('data-animating')
        this.syncSlideState(this.currentIndex)
        gsap.set(fromSlide, { autoAlpha: 0, xPercent: 0, zIndex: 0 })
        gsap.set(toSlide, { autoAlpha: 1, xPercent: 0, zIndex: 1 })
      }
    })

    const toImage = toSlide.querySelector('.hero-slider__media')
    const toImageInner =
      toImage?.querySelector('img, video') || toImage?.querySelector('.media')

    const animateItems = toSlide.querySelectorAll('[data-animate]')
    const groups = {}

    animateItems.forEach((item) => {
      const order = Number.parseInt(item.dataset.animate, 10) || 99
      if (!groups[order]) {
        groups[order] = []
      }
      groups[order].push(item)
    })

    const sortedKeys = Object.keys(groups).sort((a, b) => a - b)

    const imageOrigin = motionDirection > 0 ? '0% 50%' : '100% 50%'

    gsap.set(toSlide, {
      autoAlpha: 1,
      perspective: 1000,
      xPercent: 100 * motionDirection,
      zIndex: 1
    })
    gsap.set(fromSlide, { autoAlpha: 1, xPercent: 0, zIndex: 0 })

    timeline
      .to(fromSlide, { xPercent: -100 * motionDirection }, 0)
      .to(toSlide, { xPercent: 0 }, 0)

    if (toImage) {
      timeline.fromTo(
        toImage,
        {
          autoAlpha: 1,
          rotateY: -14 * motionDirection,
          rotateZ: 1.2 * motionDirection,
          scale: 0.95,
          transformOrigin: imageOrigin,
          xPercent: 10 * motionDirection,
          yPercent: 4 * motionDirection
        },
        {
          autoAlpha: 1,
          duration: durationDefault,
          ease: animPrimary,
          rotateY: 0,
          rotateZ: 0,
          scale: 1,
          xPercent: 0,
          yPercent: 0
        },
        0
      )
    }

    if (toImageInner) {
      timeline.fromTo(
        toImageInner,
        {
          rotateZ: -0.8 * motionDirection,
          scale: 1.08,
          yPercent: 5 * motionDirection
        },
        {
          duration: durationDefault,
          ease: animPrimary,
          rotateZ: 0,
          scale: 1,
          yPercent: 0
        },
        0.05
      )
    }

    sortedKeys.forEach((key, i) => {
      const group = groups[key]
      const firstItem = group[0]
      const type = firstItem.dataset.animateType

      const position = i === 0 ? '<+=0.2' : '<+=0.075'

      if (type === 'split') {
        const chars = group.flatMap((item) =>
          Array.from(item.querySelectorAll('.char'))
        )
        if (chars.length) {
          timeline.fromTo(
            chars,
            { autoAlpha: 0, x: '-0.35em' },
            {
              autoAlpha: 1,
              ease: animPrimary,
              stagger: { each: 0.015, from: 'end' },
              x: '0em'
            },
            position
          )
        }
      } else {
        timeline.fromTo(
          group,
          { autoAlpha: 0, x: '-3em' },
          {
            autoAlpha: 1,
            stagger: staggerDefault,
            x: '0em'
          },
          position
        )
      }
    })
  }

  syncSlideState = (activeIndex, leavingIndex = -1) => {
    this.slides.forEach((slide, index) => {
      const isActive = index === activeIndex
      const isLeaving = index === leavingIndex

      slide.classList.toggle('is-active', isActive)
      slide.classList.toggle('is-leaving', isLeaving)
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
      slide.toggleAttribute('inert', !isActive)
    })
  }

  dispatchSlideChange = () => {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        bubbles: true,
        detail: { currentPage: this.currentIndex }
      })
    )
  }

  initSplitText = () => {
    if (this.splitInitialized || typeof SplitText === 'undefined') {
      return
    }

    this.slides.forEach((slide) => {
      this.createSplit(slide)
    })
    this.splitInitialized = true
  }

  createSplit = (slide) => {
    if (!slide || slide.split || typeof SplitText === 'undefined') {
      return
    }

    const heading = slide.querySelector('h1, h2, h3, h4, h5, h6, .heading')
    if (!heading) {
      return
    }

    slide.split = new SplitText(heading, {
      charsClass: 'char',
      type: 'words, chars',
      wordsClass: 'split-word'
    })
  }

  updateDots = () => {
    this.dots?.forEach((dot) => {
      const dotIndex = Number.parseInt(dot.dataset.index, 10)
      if (Number.isNaN(dotIndex)) {
        return
      }
      dot.setAttribute(
        'aria-current',
        dotIndex === this.currentIndex ? 'true' : 'false'
      )
    })
  }

  playPlayer = () => {
    this.isPlaying = true
    if (this.timer) {
      clearInterval(this.timer)
    }
    this.timer = setInterval(() => this.next(), this.speed)
  }

  pausePlayer = () => {
    this.isPlaying = false
    if (this.timer) {
      clearInterval(this.timer)
    }
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer)
    }
    this.unobserve?.()
    this.removeEventListener('keydown', this.handleKeyDown)
    this.removeEventListener('click', this.handleClick, true)
    if (this.touchTargets.length) {
      this.touchTargets.forEach((target) => {
        target.removeEventListener('touchstart', this.handleTouchStart)
        target.removeEventListener('touchend', this.handleTouchEnd)
        target.removeEventListener('touchcancel', this.handleTouchEnd)
      })
    }
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlideshow)
}
